/**
 * Typed access to the JSON API.
 *
 * Every failure arrives as an `ApiError` carrying the server's code, message and — for
 * an invalid plan — the path to the offending predicate. That path is the reason this
 * wrapper exists at all: it is what lets a screen mark the criterion that is wrong
 * instead of showing a banner the user cannot act on.
 */

export interface FieldSpec {
  key: string;
  label: string;
  datatype: string;
  costClass: 'free' | 'computed' | 'metered' | 'ai';
  isBound: boolean;
  isVirtual: boolean;
  freshness: string | null;
  populated: number;
}

export interface Predicate {
  op: 'cmp';
  field: string;
  cmp: string;
  value?: unknown;
  proxyFor?: string;
}
export type FilterNode =
  | { op: 'and'; children: FilterNode[] }
  | { op: 'or'; children: FilterNode[] }
  | { op: 'not'; child: FilterNode }
  | Predicate;

export interface Plan {
  version: number;
  entityType: 'company';
  filters: FilterNode | null;
  fields: string[];
  unsatisfiable: { text: string; reason: string }[];
}

export interface FieldCoverage {
  field: string;
  costClass: string;
  isBound: boolean;
  populated: number;
  missing: number;
  ratio: number;
  trustworthy: boolean;
  costToFill: number | null;
  filtered: boolean;
}

export interface Coverage {
  population: number;
  fields: FieldCoverage[];
  untrustworthyFilters: string[];
  costToTrust: number;
  resultsComplete: boolean;
  unsatisfiable: { text: string; reason: string }[];
}

export interface Preview {
  matched: number;
  resultsComplete: boolean;
  coverage: Coverage;
  enrichment: {
    jobs: { call: string; costClass: string; fields: string[]; entityIds: unknown; unitCost: number; estimatedCost: number }[];
    estimatedCost: number;
    unfillable: { field: string; reason: string }[];
  };
  unsatisfiable: { text: string; reason: string }[];
  explanation: string[];
  sample: Record<string, unknown>[];
  summary: string;
}

export interface BaseSummary {
  id: number;
  name: string;
  request: string | null;
  members: number;
  updatedAt: string;
  lastEvaluated: {
    ranAt: string;
    matched: number;
    entered: number;
    exited: number;
    resultsComplete: boolean;
  } | null;
}

export interface CustomColumn {
  id: number;
  name: string;
  prompt: string;
  outputType: 'boolean' | 'number' | 'text';
}

export interface CellValue {
  value: unknown;
  source: string;
  confidence: string | number;
  observedAt: string;
}

export interface Row {
  id: number;
  values: Record<string, CellValue>;
  custom: Record<string, { value: unknown; confidence: string; evidence: string | null; observedAt: string }>;
}

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly path: string | undefined;
  constructor(status: number, code: string, message: string, path?: string) {
    super(message);
    this.status = status;
    this.code = code;
    this.path = path;
  }
}

async function call<T>(method: string, path: string, payload?: unknown): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      method,
      ...(payload === undefined
        ? {}
        : { headers: { 'content-type': 'application/json' }, body: JSON.stringify(payload) }),
    });
  } catch {
    // A network failure is not a server error and must not be reported as one: the
    // remedy is different, and "check your connection" is actionable.
    throw new ApiError(0, 'offline', 'Could not reach the server. Check your connection.');
  }

  const text = await response.text();
  let body: unknown = null;
  try {
    body = text === '' ? null : JSON.parse(text);
  } catch {
    throw new ApiError(response.status, 'bad_response', `The server returned something unreadable (${String(response.status)}).`);
  }

  if (!response.ok) {
    const err = (body as { error?: { code?: string; message?: string; path?: string } } | null)?.error;
    throw new ApiError(
      response.status,
      err?.code ?? 'error',
      err?.message ?? `Request failed (${String(response.status)}).`,
      err?.path,
    );
  }
  return body as T;
}

export interface SessionInfo {
  signedIn: boolean;
  user?: { id: number; email: string; role: string; hasPassword?: boolean; passwordSetAt?: string | null };
  orgId?: number;
  orgs?: { orgId: number; name: string; role: string }[];
  plan?: { code: string; name: string; maxBases: number | null } | null;
  status?: string | null;
  /** Present only while a trial is live: what is left of it, in days and in credit. */
  trial?: {
    endsAt: string;
    daysLeft: number;
    creditRemaining: number;
    creditGranted: number;
  } | null;
  entitlements?: Record<string, { allowed: boolean; reason: string; used?: number; limit?: number | null }>;
}

export interface EnrichmentProgress {
  pending: number;
  running: number;
  done: number;
  skipped: number;
  failed: number;
  dead: number;
  total: number;
  active: boolean;
  errors: { fields: string[]; error: string; state: string }[];
}

export interface PlanInfo {
  code: string;
  name: string;
  priceCents: number;
  listed: boolean;
  maxBases: number | null;
  maxRecords: number | null;
  maxCustomColumns: number | null;
  monthlyCredits: number;
  refreshInterval: string | null;
  apiAccess: boolean;
}

export const api = {
  plans: () => call<{ plans: PlanInfo[] }>('GET', '/api/plans'),
  checkout: (plan: string) => call<{ url: string }>('POST', '/api/billing/checkout', { plan }),
  portal: () => call<{ url: string }>('POST', '/api/billing/portal', {}),

  session: () => call<SessionInfo>('GET', '/api/auth/session'),
  requestLink: (email: string, orgName?: string, password?: string) =>
    call<{ sent: boolean; delivery: string; message: string }>('POST', '/api/auth/request', {
      email,
      ...(orgName === undefined ? {} : { orgName }),
      ...(password === undefined || password === '' ? {} : { password }),
    }),
  signIn: (email: string, password: string) =>
    call<{ userId: number; orgId: number; email: string }>('POST', '/api/auth/signin', { email, password }),
  redeem: (token: string) =>
    call<{ userId: number; orgId: number; email: string }>('POST', '/api/auth/callback', { token }),
  setPassword: (password: string, current?: string) =>
    call<{ hasPassword: boolean; otherSessionsSignedOut: number }>('POST', '/api/auth/password', {
      password,
      ...(current === undefined || current === '' ? {} : { current }),
    }),
  clearPassword: (current: string) =>
    call<{ hasPassword: boolean }>('DELETE', '/api/auth/password', { current }),
  signOut: () => call<{ signedOut: boolean }>('POST', '/api/auth/signout'),

  fields: () => call<{ total: number; fields: FieldSpec[] }>('GET', '/api/fields'),

  plan: (request: string) => call<{ plan: Plan; request: string }>('POST', '/api/plan', { request }),
  preview: (plan: Plan, sampleSize = 5) =>
    call<Preview>('POST', '/api/plan/preview', { plan, sampleSize }),
  explain: (plan: Plan) =>
    call<{ lines: string[]; coverage: Coverage }>('POST', '/api/plan/explain', { plan }),
  edit: (payload: Record<string, unknown>) => call<{ plan: Plan }>('POST', '/api/plan/edit', payload),

  bases: () => call<{ bases: BaseSummary[] }>('GET', '/api/bases'),
  createBase: (name: string, plan: Plan, request?: string) =>
    call<{ base: { id: number; name: string }; evaluation: { matched: number } }>('POST', '/api/bases', {
      name,
      plan,
      request,
    }),
  base: (id: number) =>
    call<{
      base: { id: number; name: string; request: string | null; plan: Plan };
      members: number;
      columns: CustomColumn[];
      evaluations: { id: number; ranAt: string; matched: number; entered: number; exited: number; resultsComplete: boolean }[];
    }>('GET', `/api/bases/${String(id)}`),
  patchBase: (id: number, changes: Record<string, unknown>) =>
    call<{ base: { id: number; name: string } }>('PATCH', `/api/bases/${String(id)}`, changes),
  deleteBase: (id: number) => call<{ deleted: boolean }>('DELETE', `/api/bases/${String(id)}`),
  evaluate: (id: number) =>
    call<{ evaluation: { matched: number; entered: number[]; exited: number[]; resultsComplete: boolean }; coverage: Coverage }>(
      'POST',
      `/api/bases/${String(id)}/evaluate`,
    ),

  rows: (id: number, limit: number, offset: number) =>
    call<{ rows: Row[]; total: number; fields: string[]; columns: CustomColumn[] }>(
      'GET',
      `/api/bases/${String(id)}/rows?limit=${String(limit)}&offset=${String(offset)}`,
    ),

  provenance: (entityId: number, field: string) =>
    call<{
      observations: {
        value: unknown;
        confidence: string;
        evidence: string | null;
        observed_at: string;
        superseded_at: string | null;
        snapshot_hash: string | null;
        source_key: string;
        source_name: string;
        trust_rank: number;
      }[];
      changes: { kind: string; old_value: unknown; new_value: unknown; detected_at: string; source_key: string }[];
    }>('GET', `/api/entities/${String(entityId)}/fields/${field}`),

  addColumn: (baseId: number, column: { name: string; prompt: string; outputType: string }) =>
    call<{ column: CustomColumn; backfill: { entityIds: number[]; alreadyAnswered: number; estimatedCost: number } }>(
      'POST',
      `/api/bases/${String(baseId)}/columns`,
      column,
    ),
  removeColumn: (baseId: number, columnId: number) =>
    call<{ detached: boolean }>('DELETE', `/api/bases/${String(baseId)}/columns/${String(columnId)}`),

  pending: (baseId: number) =>
    call<{
      backfill: { column: CustomColumn; entityIds: number; alreadyAnswered: number; estimatedCost: number; runnable: boolean }[];
      enrichment: { jobs: { call: string; costClass: string; fields: string[]; entityIds: number; estimatedCost: number; runnable: boolean }[]; estimatedCost: number; unfillable: { field: string; reason: string }[] };
      coverage: Coverage;
    }>('GET', `/api/bases/${String(baseId)}/pending`),

  enrich: (baseId: number) =>
    call<{ queued: number; estimatedCost: number; refused: { call: string; fields: string[]; entityIds: number; reason: string }[] }>(
      'POST',
      `/api/bases/${String(baseId)}/enrich`,
      {},
    ),

  enrichment: (baseId: number) =>
    call<EnrichmentProgress>('GET', `/api/bases/${String(baseId)}/enrichment`),

  exportBase: (baseId: number, withProvenance: boolean) =>
    call<{
      id: number;
      rows: number;
      omitted: { field: string; reason: string }[];
      filename: string;
      dataUrl: string;
      noticeUrl: string;
    }>('POST', `/api/bases/${String(baseId)}/export`, { withProvenance }),

  changes: (baseId: number) =>
    call<{
      membership: { since: string; entered: { entityId: number; at: string }[]; exited: { entityId: number; at: string }[] } | null;
      fields: { entity_id: number; field_key: string; kind: string; old_value: unknown; new_value: unknown; detected_at: string; source_key: string; name: string | null }[];
    }>('GET', `/api/bases/${String(baseId)}/changes`),
};
